import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';
import ServersSection from './components/ServersSection';
import ActivenessSection from './components/ActivenessSection';
import RankupSection from './components/RankupSection';
import RevenueTargets from './components/RevenueTargets';

// ─── Toast Component ──────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div
      className="fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl border"
      style={{
        backgroundColor: '#111111',
        borderColor: type === 'success' ? '#22c55e40' : '#ef444440',
        borderLeft: `3px solid ${type === 'success' ? '#22c55e' : '#ef4444'}`,
        fontFamily: 'Inter, sans-serif',
        minWidth: 260,
        maxWidth: 400,
      }}
    >
      <span style={{ fontSize: 16 }}>{type === 'success' ? '✅' : '❌'}</span>
      <span className="text-sm text-white/80 flex-1">{message}</span>
      <button onClick={onClose} className="text-white/30 hover:text-white/60 text-lg leading-none ml-2">×</button>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ConfigurationManagement = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [servers, setServers] = useState([]);
  const [serversLoading, setServersLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user?.email) setUserEmail(session?.user?.email);
    });
  }, []);

  const fetchServers = useCallback(async () => {
    setServersLoading(true);
    const { data, error } = await supabase?.from('servers')?.select('*')?.order('created_at');
    if (!error && data) {
      setServers(data);
    }
    setServersLoading(false);
  }, []);

  useEffect(() => {
    fetchServers();
  }, [fetchServers]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, id: Date.now() });
  }, []);

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <div className="hidden md:flex">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(v => !v)}
          userEmail={userEmail}
        />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.config.title', 'Configuration Serveur')} />
        {/* Header */}
        <div
          className="px-8 py-6 border-b sticky top-0 z-10"
          style={{ backgroundColor: 'var(--color-background)', borderColor: '#1f1f1f' }}
        >
          <h1 className="text-white text-2xl font-bold" style={{ fontFamily: 'Sora, sans-serif' }}>
            {t('admin.config.title', 'Configuration Serveur')}
          </h1>
          <p className="text-white/40 text-sm mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('admin.config.subtitle', 'Paramètres des objectifs TikTok par serveur')}
          </p>
        </div>

        {/* Content */}
        <div className="px-8 py-6 flex flex-col gap-6">
          {/* SECTION 1: Servers */}
          <ServersSection
            servers={servers}
            onServersChange={fetchServers}
            showToast={showToast}
          />

          {/* SECTION 2: Activeness */}
          <ActivenessSection
            servers={servers}
            showToast={showToast}
          />

          {/* SECTION 3: Rankup */}
          <RankupSection
            servers={servers}
            showToast={showToast}
          />

          {/* SECTION 4: Incremental Revenue */}
          <RevenueTargets
            servers={servers}
            showToast={showToast}
          />
        </div>
      </main>
      {/* Toast */}
      {toast && (
        <Toast
          key={toast?.id}
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
      <AdminBottomNav />
    </div>
  );
};

export default ConfigurationManagement;